import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
    INSTITUTION_KEYS,
    institutionLabel,
} from '@/shared/constants/institutions';
import guidelinesService from '@/shared/services/guidelinesService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { coordinatedInstitution } from '@/shared/utils/roles';
import { renderMarkdown } from '@/shared/utils/markdown';
import { serverMessage } from '@/shared/utils/serverMessage';

/**
 * The sections of the document, in the order they are read.
 *
 * A member sees the shared section followed by their own institution's, so
 * the shared one is first here as well. Every institution has a section
 * whether or not it carries text, because the route answers with all four
 * keys and an empty one is how a campus says it has nothing to add.
 */
const SHARED = Object.freeze({
    key: 'shared',
    title: 'Shared',
    label: 'Shared guidelines',
    save: 'Save the shared guidelines',
});

const SECTIONS = Object.freeze([
    SHARED,
    ...INSTITUTION_KEYS.map((key) =>
        Object.freeze({
            key,
            title: institutionLabel(key),
            label: `${institutionLabel(key)} guidelines`,
            save: `Save the ${institutionLabel(key)} guidelines`,
        })
    ),
]);

/**
 * The body one section saves with.
 *
 * The route merges a partial body: a section the body does not name keeps
 * what it holds. Sending the whole document instead would let two
 * coordinators editing different campuses overwrite each other.
 */
function sectionBody(key, text) {
    return key === SHARED.key
        ? { shared: text }
        : { byInstitution: { [key]: text } };
}

function GuidelinesTab() {
    const { userProfile } = useAuth();
    // A coordinator writes their own institution's section alone; the server
    // refuses a body naming any other. The rest stay readable here, because
    // a coordinator writes theirs knowing what the shared section already says.
    const scoped = coordinatedInstitution(userProfile);
    const canEdit = (key) => !scoped || key === scoped;
    const [active, setActive] = useState(scoped || SHARED.key);
    const [drafts, setDrafts] = useState(null);
    const [updatedAt, setUpdatedAt] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saved, setSaved] = useState('');

    useEffect(() => {
        async function first() {
            try {
                const data = await guidelinesService.get();
                setDrafts({
                    [SHARED.key]: data.shared,
                    ...data.byInstitution,
                });
                setUpdatedAt(data.updatedAt);
            } catch (caught) {
                setError(
                    serverMessage(
                        caught,
                        'Could not load the reuse guidelines.'
                    )
                );
            } finally {
                setLoading(false);
            }
        }
        first();
    }, []);

    const handleSave = (key) => async () => {
        setSaveError('');
        setSaved('');
        try {
            // Only the date is taken from the response. The other sections
            // are left alone so a save here does not discard text an admin
            // has typed in another tab and not saved yet.
            const data = await guidelinesService.update(
                sectionBody(key, drafts[key])
            );
            setUpdatedAt(data.updatedAt);
            setSaved(key);
        } catch (caught) {
            setSaveError(
                serverMessage(caught, 'Could not save that section. Try again.')
            );
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading guidelines" />;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <>
            <h2 className="h4 mb-3">Reuse guidelines</h2>
            <p className="text-muted">
                A member reads the shared section followed by their own
                institution&apos;s. Markdown is rendered; anything the sanitizer
                does not allow is dropped from the preview and from the page, so
                the preview is what the member will see.
            </p>
            {saveError && <Alert variant="danger">{saveError}</Alert>}
            <Tabs
                id="admin-guidelines-tabs"
                activeKey={active}
                onSelect={(key) => setActive(key)}
                className="mb-3"
                mountOnEnter
                unmountOnExit
            >
                {SECTIONS.map((section) => (
                    <Tab
                        key={section.key}
                        eventKey={section.key}
                        title={section.title}
                    >
                        <Row className="g-3">
                            {canEdit(section.key) && (
                                <Col md={6}>
                                    <Form.Group
                                        controlId={`guidelines-${section.key}`}
                                    >
                                        <Form.Label>{section.label}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={16}
                                            value={drafts[section.key]}
                                            onChange={(event) => {
                                                setSaved('');
                                                setDrafts((previous) => ({
                                                    ...previous,
                                                    [section.key]:
                                                        event.target.value,
                                                }));
                                            }}
                                        />
                                    </Form.Group>
                                    <div className="d-flex align-items-center gap-3 mt-2">
                                        <Button
                                            aria-label={section.save}
                                            onClick={handleSave(section.key)}
                                        >
                                            Save
                                        </Button>
                                        {saved === section.key && (
                                            <span
                                                role="status"
                                                className="text-success small"
                                            >
                                                Saved
                                            </span>
                                        )}
                                    </div>
                                </Col>
                            )}
                            <Col md={canEdit(section.key) ? 6 : 12}>
                                <section
                                    aria-labelledby={`guidelines-preview-${section.key}`}
                                >
                                    <h3
                                        id={`guidelines-preview-${section.key}`}
                                        className="h6"
                                    >
                                        {canEdit(section.key)
                                            ? 'Preview'
                                            : `${section.label} (read only)`}
                                    </h3>
                                    <div
                                        className="border rounded p-3"
                                        dangerouslySetInnerHTML={{
                                            __html: renderMarkdown(
                                                drafts[section.key]
                                            ),
                                        }}
                                    />
                                </section>
                            </Col>
                        </Row>
                    </Tab>
                ))}
            </Tabs>
            <p className="text-muted small">
                <time dateTime={updatedAt}>
                    Updated {new Date(updatedAt).toLocaleDateString()}
                </time>
            </p>
        </>
    );
}

export default GuidelinesTab;
